// Libs
import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router';

// Components
const ClassSetup = lazy(() => import('./components/CreateClass/ClassSetup'));
const FpuDashboard = lazy(() => import('./index'));
const LaunchAndPromote = lazy(() => import('./components/LaunchAndPromote'));
const MyClasses = lazy(() => import('./components/MyClasses'));
const MyPeople = lazy(() => import('./components/MyPeople'));

import ErrorDisplay from '~fpu/shared/components/ErrorDisplay';
import FpuDashboardLayout from '~fpu/modules/FpuDashboard/components/FpuDashboardLayout';
import ProfileEdit from '~fpu/modules/FpuDashboard/components/CoordinatorProfile/ProfileEdit';
import RouteErrorElement from '~fpu/shared/components/RouteErrorElement';
import PromoteContent from '~fpu/modules/FpuDashboard/components/LaunchAndPromote/PromoteContent';
import Header from '~fpu/shared/components/navigation/Header';
import EditWorkshop from './components/BudgetWorkshop/EditWorkshop';

const OrganizationDashboard = lazy(
  () => import('./components/OrganizationDashboard')
);
const CoordinatorDashboard = lazy(
  () => import('./components/CoordinatorDashboard')
);
const RealStories = lazy(
  () => import('~fpu/modules/FpuDashboard/components/Testimonials/RealStories')
);
const ResourcesAndTraining = lazy(
  () => import('./components/ResourcesAndTraining/ResourcesAndTraining')
);
const OrganizationMemberships = lazy(
  () => import('./components/MyMemberships/OrganizationMemberships')
);
const FpuDashboardClassDetail = lazy(
  () => import('~fpu/modules/FpuDashboard/components/ClassDetail/index')
);

const WhatsNew = lazy(
  () => import('~fpu/modules/FpuDashboard/components/WhatsNew/WhatsNew')
);
const MembershipUtilization = lazy(
  () =>
    import('~fpu/modules/FpuDashboard/components/MembershipUtilization/MembershipUtilization')
);
const ScheduleCsCall = lazy(
  () => import('./components/ScheduleCsCall/ScheduleCsCall')
);
const BudgetWorkshop = lazy(
  () => import('./components/BudgetWorkshop/BudgetWorkshop')
);
const WorkshopDetail = lazy(
  () => import('./components/BudgetWorkshop/WorkshopDetail')
);

const ErrorElement = () => (
  <RouteErrorElement>
    {(status) => (
      <FpuDashboardLayout analyticsPageName="Error">
        <ErrorDisplay code={status} />
      </FpuDashboardLayout>
    )}
  </RouteErrorElement>
);

const FPU_DASHBOARD_ROUTES: RouteObject[] = [
  {
    path: 'dashboard',
    errorElement: <ErrorElement />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <FpuDashboard />
          </Suspense>
        )
      },
      {
        path: 'promote',
        element: (
          <Suspense fallback={null}>
            <>
              <Header />
              <div className="p-2">
                <PromoteContent />
              </div>
            </>
          </Suspense>
        )
      },
      {
        path: 'organization',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <OrganizationDashboard />
              </Suspense>
            )
          },
          {
            path: ':orgIdForInternalUse',
            element: (
              <Suspense fallback={null}>
                <OrganizationDashboard />
              </Suspense>
            )
          },
          {
            path: 'my-people',
            element: (
              <Suspense fallback={null}>
                <MyPeople />
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'coordinator',
        element: (
          <Suspense fallback={null}>
            <CoordinatorDashboard />
          </Suspense>
        )
      },
      {
        path: 'classes',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <MyClasses />
              </Suspense>
            )
          },
          {
            path: 'create',
            element: (
              <Suspense fallback={null}>
                <FpuDashboardLayout analyticsPageName="Create Class">
                  <ClassSetup />
                </FpuDashboardLayout>
              </Suspense>
            )
          },
          {
            path: ':classId',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={null}>
                    <FpuDashboardLayout analyticsPageName="Class Detail">
                      <FpuDashboardClassDetail />
                    </FpuDashboardLayout>
                  </Suspense>
                )
              },
              {
                path: 'edit',
                element: (
                  <Suspense fallback={null}>
                    <FpuDashboardLayout analyticsPageName="Edit Class">
                      <ClassSetup isEditMode />
                    </FpuDashboardLayout>
                  </Suspense>
                )
              }
            ]
          }
        ]
      },
      {
        path: 'whats-new',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="What's New">
              <WhatsNew />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'membership-utilization',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="Ministry Benefits">
              <MembershipUtilization />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'real-stories',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="Testimonials">
              <RealStories />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'launch-and-promote',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="Launch And Promote">
              <LaunchAndPromote />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'resources-and-training',
        element: (
          <Suspense fallback={null}>
            <ResourcesAndTraining />
          </Suspense>
        )
      },
      {
        path: 'send-memberships',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="My Memberships">
              <OrganizationMemberships />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'schedule-cs-call',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="Schedule CS Call">
              <ScheduleCsCall />
            </FpuDashboardLayout>
          </Suspense>
        )
      },
      {
        path: 'budget-workshop',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={null}>
                <FpuDashboardLayout analyticsPageName="Budget Workshop">
                  <BudgetWorkshop />
                </FpuDashboardLayout>
              </Suspense>
            )
          },
          {
            path: ':workshopId',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={null}>
                    <FpuDashboardLayout analyticsPageName="Budget Workshop Detail">
                      <WorkshopDetail />
                    </FpuDashboardLayout>
                  </Suspense>
                )
              },
              {
                path: 'edit',
                element: (
                  <Suspense fallback={null}>
                    <FpuDashboardLayout analyticsPageName="Edit Budget Workshop">
                      <EditWorkshop />
                    </FpuDashboardLayout>
                  </Suspense>
                )
              }
            ]
          }
        ]
      },
      {
        path: 'profile-edit',
        element: (
          <Suspense fallback={null}>
            <FpuDashboardLayout analyticsPageName="Profile Edit">
              <ProfileEdit />
            </FpuDashboardLayout>
          </Suspense>
        )
      }
    ]
  }
];

export default FPU_DASHBOARD_ROUTES;
